import {
  DiscoveryApi,
  FetchApi,
  createApiRef,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';

import { Prototype, UxEvaluation } from '../models/types';

export interface PrototypeViewerApi {
  listPrototypes(): Promise<Prototype[]>;
  getPrototype(id: string): Promise<Prototype>;
  getVersions(id: string): Promise<Prototype[]>;
  uploadPrototype(file: File, metadata: UploadMetadata): Promise<Prototype>;
  deletePrototype(id: string): Promise<void>;
  updateStatus(id: string, status: string): Promise<void>;
  getEvaluations(prototypeId: string): Promise<UxEvaluation[]>;
  createEvaluation(evaluation: CreateEvaluationRequest): Promise<UxEvaluation>;
  deleteEvaluation(id: string): Promise<void>;
}

export interface UploadMetadata {
  title: string;
  description: string;
  team: string;
  author: string;
  tags: string[];
  status: string;
  groupId?: string;
  versionLabel?: string;
}

export interface CreateEvaluationRequest {
  prototypeId: string;
  evalMode: string;
  featureArea: string;
  userTask: string;
  evaluatorName: string;
  evaluatorTeam: string;
  findings: any;
  heuristicScores: any;
  receipt: any;
}

export const PROTOTYPE_VIEWER_API_REF = createApiRef<PrototypeViewerApi>({
  id: 'plugin.prototype-viewer.api',
});

export class PrototypeViewerClient implements PrototypeViewerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('prototype-viewer');
  }

  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.baseUrl();
    const url = `${base}${path}`;
    const response = await this.fetchApi.fetch(url, init);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  }

  private async resolveHtmlUrl(proto: Prototype): Promise<Prototype> {
    if (!proto.htmlUrl) return proto;
    const base = await this.baseUrl();
    return { ...proto, htmlUrl: `${base}${proto.htmlUrl}` };
  }

  private async resolveHtmlUrls(protos: Prototype[]): Promise<Prototype[]> {
    const base = await this.baseUrl();
    return protos.map(p =>
      p.htmlUrl ? { ...p, htmlUrl: `${base}${p.htmlUrl}` } : p,
    );
  }

  async listPrototypes(): Promise<Prototype[]> {
    const protos = await this.fetch<Prototype[]>('/prototypes');
    return this.resolveHtmlUrls(protos);
  }

  async getPrototype(id: string): Promise<Prototype> {
    const proto = await this.fetch<Prototype>(
      `/prototypes/${encodeURIComponent(id)}`,
    );
    return this.resolveHtmlUrl(proto);
  }

  async getVersions(id: string): Promise<Prototype[]> {
    const protos = await this.fetch<Prototype[]>(
      `/prototypes/${encodeURIComponent(id)}/versions`,
    );
    return this.resolveHtmlUrls(protos);
  }

  async uploadPrototype(
    file: File,
    metadata: UploadMetadata,
  ): Promise<Prototype> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', metadata.title);
    formData.append('description', metadata.description);
    formData.append('team', metadata.team);
    formData.append('author', metadata.author);
    formData.append('tags', JSON.stringify(metadata.tags));
    formData.append('status', metadata.status);
    if (metadata.groupId) formData.append('groupId', metadata.groupId);
    if (metadata.versionLabel)
      formData.append('versionLabel', metadata.versionLabel);

    const proto = await this.fetch<Prototype>('/prototypes', {
      method: 'POST',
      body: formData,
    });
    return this.resolveHtmlUrl(proto);
  }

  async deletePrototype(id: string): Promise<void> {
    return this.fetch(`/prototypes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async updateStatus(id: string, status: string): Promise<void> {
    return this.fetch(`/prototypes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
  }

  async getEvaluations(prototypeId: string): Promise<UxEvaluation[]> {
    return this.fetch(
      `/evaluations/prototype/${encodeURIComponent(prototypeId)}`,
    );
  }

  async createEvaluation(
    evaluation: CreateEvaluationRequest,
  ): Promise<UxEvaluation> {
    return this.fetch('/evaluations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(evaluation),
    });
  }

  async deleteEvaluation(id: string): Promise<void> {
    return this.fetch(`/evaluations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
}
